import { z } from 'zod/mini'

import { amount, eventValue, itemObject } from './fields'
import { gtmMetadataShape } from './gtm-metadata'
import { nonEmptyString } from './non-empty-string'

/**
 * `add_to_cart` is built by several producers (product page, recommendation
 * cards, bundles, favorites, cart replacement) that don't all have the full
 * product data, so descriptive fields are optional and ids accept numbers.
 */
const addToCartItemSchema = itemObject({
  item_id: z.string(),
  item_name: z.string(),
  index: z.number().check(z.gte(0)),
  location_id: nonEmptyString(),
  price: amount(),
  quantity: z.number().check(z.gt(0)),
  google_business_vertical: z.literal('retail'),
  // Zone or list passed by every producer
  item_list_id: z.string(),
  item_list_name: z.string(),

  // A number for cart replacement, empty when unknown
  item_legacy_id: z.union([z.string(), z.number()]),
  // A number for product page and cart replacement, missing for favorites
  item_variant: z.optional(z.union([z.string(), z.number()])),
  // Missing or empty depending on the producer
  item_brand: z.optional(z.string()),
  item_category: z.optional(z.string()),
  item_warranty_duration: z.optional(z.string()),
  affiliation: z.optional(z.string()),

  // Always set by trackAddToCart, null when the caller has no value
  discount: amount(),
  item_insurance_price: amount(),
  item_mobile_plan_monthly_price: amount(),
  item_shipping_cost: amount(),
})

export const addToCartSchema = z.strictObject({
  ...gtmMetadataShape,
  event: z.literal('add_to_cart'),
  add_to_cart_location: nonEmptyString(),
  // Missing for cart replacement, which doesn't know the cart trade-in state
  has_trade_in: z.optional(z.boolean()),
  ecommerce: z.strictObject({
    currency: nonEmptyString(),
    value: eventValue(),
    items: z.array(addToCartItemSchema).check(z.minLength(1)),
  }),
})
